import { MultiValueSearch, SearchSuggestion } from '@ver-uds/react';
import { Accordion, AccordionProvider, ComboBoxControl, DropdownControl, Form } from '@ver-uds/uswds-react';
import { AccountSearchParametersQuery, AccountStatus } from 'eve-account-client';
import React, { Dispatch, SetStateAction, useContext } from 'react';
import LoaderIcon from 'uswds/dist/img/loader.svg';
import { useStatesQuery } from '../../services';
import FiltersButton from './FiltersButton';
import ManageCompaniesContext from '../../context/ManageCompanies/ManageCompaniesContext';

export type ManageCompaniesFormProps = {
  setQuery: Dispatch<SetStateAction<AccountSearchParametersQuery>>;
};

const searchOptions: SearchSuggestion[] = [
  {
    label: 'Company Name',
    value: 'companyName',
    pattern: /^[^^()|'‘’‛`]{1,100}$/,
  },
  {
    label: 'Company ID',
    value: 'accountId',
    pattern: /^([0-9_-]){1,10}$/,
  },
  {
    label: 'Street Address',
    value: 'streetAddress',
    pattern: /^[^()|'‘’‛`]{1,60}$/,
  },
  {
    label: 'City',
    value: 'city',
    pattern: /^[a-zA-Z0-9\-‒–—― ]{1,30}$/,
  },
  {
    label: 'Zip Code',
    value: 'zipCode',
    pattern: /^\d{5}$/,
  },
];

const ManageCompaniesForm = (): JSX.Element => {
  const { data: states = [], isLoading: statesIsLoading } = useStatesQuery();
  const { setQuery } = useContext(ManageCompaniesContext);

  return (
    <Form className="maxw-none border-bottom border-gray-30 padding-bottom-5" noValidate>
      <AccordionProvider initialExpanded="filters">
        <div className="grid-row grid-gap">
          <div className="flex-fill">
            <MultiValueSearch
              label="Search (Company Name, Company ID, Street Address, City, Zip Code)"
              id="company-search"
              onChange={(values) =>
                setQuery((current) => ({
                  ...current,
                  ...searchOptions.reduce((aggregator, { value: queryOption }) => {
                    const newValue = values?.find(({ value: option }) => option === queryOption)?.text?.raw;

                    return {
                      ...aggregator,
                      ...(queryOption === 'accountId'
                        ? { accountIds: newValue ? [newValue] : undefined }
                        : { [queryOption]: newValue }),
                    };
                  }, {}),
                }))
              }
              options={searchOptions}
            />
          </div>
          <div className="tablet:grid-col-auto display-flex flex-row flex-align-end flex-justify-center">
            <FiltersButton controls="search-filters" />
          </div>
        </div>
        <Accordion.Content as="div" id="search-filters" className="padding-0 overflow-visible">
          <div className="grid-row grid-gap">
            <div className="tablet:grid-col-4 desktop:grid-col-3">
              <DropdownControl
                id="company-status"
                name="status"
                label="Company Status"
                className="maxw-none"
                onChange={({ target: { value: status } }) =>
                  setQuery((current) => ({
                    ...current,
                    status: status !== '' ? (status as AccountStatus) : undefined,
                  }))
                }
              >
                <option value="">All Statuses</option>
                <option value={AccountStatus.TERMINATIONREQUESTED}>Requested Termination</option>
                <option value={AccountStatus.PENDING}>Pending Registration</option>
                <option value={AccountStatus.TERMINATED}>Terminated</option>
                <option value={AccountStatus.ACTIVE}>Active</option>
                <option value={AccountStatus.REJECTED}>Rejected</option>
              </DropdownControl>
            </div>
            <div className="tablet:grid-col-5 desktop:grid-col-3">
              {statesIsLoading ? (
                <div>
                  Loading… <img src={LoaderIcon} alt="" className="height-2" />
                </div>
              ) : (
                <ComboBoxControl
                  id="company-state"
                  name="stateCode"
                  label="State in Physical Address"
                  className="maxw-none tablet:grid-col-10 desktop:grid-col-12"
                  defaultValue="*"
                  onChange={({ target: { value: stateCode } }) =>
                    setQuery((current) => ({
                      ...current,
                      stateCode: !['', '*'].includes(stateCode) ? stateCode : undefined,
                    }))
                  }
                >
                  {[
                    <option key="*" value="*">
                      All States
                    </option>,
                    ...states.map((state) => (
                      <option key={state.code} value={state.code}>
                        {state.displayName} ({state.code})
                      </option>
                    )),
                  ]}
                </ComboBoxControl>
              )}
            </div>
          </div>
        </Accordion.Content>
      </AccordionProvider>
    </Form>
  );
};

export default ManageCompaniesForm;
